import React, { useEffect, useState } from 'react';

import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
  useLocation,
} from 'react-router-dom';

import { useSelector } from 'react-redux';

import api from './utils/apiWrapper';
import { isCurrentDateInRange } from './utils/convertDateFormatUtils';

const FAQ = React.lazy(() => import('./pages/bulletinboard/faq/faqs'));

const Login = React.lazy(() => import('./pages/login-page/loginPage'));

const Layout = React.lazy(() => import('./layouts/Layout'));

const Reports = React.lazy(() => import('./pages/Reports/Reports'));

const Disclaimer = React.lazy(() => import('./pages/disclaimer/disclaimer'));

const EmergencyGrant = React.lazy(
  () => import('./pages/grantAuthorizations/emergencygrant')
);

const OutagePage = React.lazy(() => import('./pages/outagePage/outagePage'));

const DSSLoginPage = React.lazy(() => import('./pages/login-page/dssloginPage'));

const NoAccessPage = React.lazy(() => import('./pages/error-page/NoAccessPage'));

const BulletInBoard = React.lazy(() => import('./pages/bulletinboard/BulletInBoard'));

const AccountDetails = React.lazy(
  () => import('./pages/account-inquiry/account-details/AccountDetails')
);

const AccountInquiryForm = React.lazy(
  () => import('./pages/account-inquiry/account-inquiry-form/AccountInquiryForm')
);

const HeapGrant = React.lazy(() => import('./pages/grantAuthorizations/heapgrant'));

const AccountDetailsTable = React.lazy(
  () => import('./pages/account-inquiry/account-details-table/AvailableAccountsTable')
);

const TenDayHold = React.lazy(() => import('./pages/appointments/tenDayHold'));

const ContactUs = React.lazy(() => import('./pages/contactUs/contactUs'));
const FemaGrant = React.lazy(() => import('./pages/otherGrant/femaGrant/FemaGrant'));
const CareAndShare = React.lazy(() => import('./pages/otherAgencyGrants/careandshare'));

const NewReferralForm = React.lazy(
  () => import('./pages/referral-forms/new-referral-forms/NewReferralForm')
);

const StartDirectVoucher = React.lazy(
  () => import('./pages/programs/direct-voucher/start-direct-voucher/startDirectVoucher')
);

const StopDirectVoucher = React.lazy(
  () => import('./pages/programs/direct-voucher/stop-direct-voucher/stopDirectVoucher')
);

const TransferDirectVoucher = React.lazy(
  () =>
    import(
      './pages/programs/direct-voucher/transfer-direct-voucher/transferDirectVoucher'
    )
);

const NewReferralFormList = React.lazy(
  () => import('./pages/referral-forms/new-referral-forms/NewReferralFormList')
);

const StartGuarantee = React.lazy(
  () => import('./pages/programs/guarantee/start-guarantee/startGuarantee')
);

const StopGuarantee = React.lazy(
  () => import('./pages/programs/guarantee/stop-guarantee/stopGuarantee')
);

const PreviouslyViewedForm = React.lazy(
  () => import('./pages/referral-forms/previously-viewed-forms/PreviouslyViewedForm')
);

const PreviouslyViewedFormList = React.lazy(
  () => import('./pages/referral-forms/previously-viewed-forms/PreviouslyViewedFormList')
);

const Outage = React.lazy(() => import('./pages/outagePage/outageForm'));

const OutageView = React.lazy(() => import('./pages/outagePage/OutageView'));

const DisclaimerSave = React.lazy(() => import('./pages/disclaimer/DisclaimerSave'));

const DisclaimerView = React.lazy(() => import('./pages/disclaimer/DisclaimerView'));

const BulletInBoardView = React.lazy(
  () => import('./pages/bulletinboard/BulletInBoardView')
);

const BulletInBoardSave = React.lazy(
  () => import('./pages/bulletinboard/BulletInBoardSave')
);

const ProtectedRoute = ({ children, allowedRoles }) => {
  const groupId = useSelector((state) => state.session.groupId);
  const normalizedGroupId = groupId ? String(groupId).trim() : '';
  const normalizedAllowedRoles = allowedRoles.map((role) => String(role).trim());
  const location = useLocation();
  const isRehydrated = useSelector((state) => state?.session?.accessToken);
  const [outageDisplay, setOutageDisplay] = useState('');

  useEffect(() => {
    const fetchOutageResponse = async () => {
      try {
        const result = await api.getRequest('common/AdminContent?PageName=Outage');
        if (result?.statusCode === 200) {
          setOutageDisplay(
            result?.data?.textContent &&
              isCurrentDateInRange(result?.data?.fromDate, result?.data?.toDate)
          );
        } else setOutageDisplay('');
      } catch (error) {
        console.error('Error fetchOutageResponse:', error);
      }
    };
    if (isRehydrated) fetchOutageResponse();
  }, [isRehydrated]);

  if (
    outageDisplay &&
    !(
      location.pathname === '/outage/save' ||
      location.pathname === '/disclaimer/save' ||
      location.pathname === '/bulletInBoard/save'
    )
  ) {
    return <Navigate to='/outage' replace />;
  }

  if (!normalizedGroupId) {
    return <Navigate to='/access-denied' replace />;
  }

  const hasAllowedRole = normalizedGroupId
    .split(',')
    .some((item) => normalizedAllowedRoles.includes(item));

  if (!hasAllowedRole) {
    return <Navigate to='/access-denied' replace />;
  }

  return children;
};

const RouteConfig = () => {
  const accessGroup = process.env.REACT_APP_DSS_GOVT_BASIC_ACCESS;
  const fullAccessGroup = process.env.REACT_APP_DSS_GOVT_FULL_ACCESS;
  const adminAccessGroup = process.env.REACT_APP_DSS_GOVT_ADMIN_ACCESS;

  return (
    <Routes>
      <Route path='/' element={<Login />} />
      <Route path='/dsslogin' element={<DSSLoginPage />} />
      <Route
        path='/access-denied'
        element={
          <Layout>
            <NoAccessPage />
          </Layout>
        }
      />
      <Route
        path='/reports'
        element={
          <ProtectedRoute allowedRoles={[accessGroup, fullAccessGroup]}>
            <Layout>
              <Reports />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path='/disclaimer'
        element={
          <ProtectedRoute allowedRoles={[accessGroup, fullAccessGroup, adminAccessGroup]}>
            <Disclaimer />
          </ProtectedRoute>
        }
      />
      <Route
        path='/bulletinboard'
        element={
          <ProtectedRoute allowedRoles={[fullAccessGroup]}>
            <Layout>
              <BulletInBoard />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path='/faqs'
        element={
          <ProtectedRoute allowedRoles={[fullAccessGroup]}>
            <Layout>
              <FAQ />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path='/heapgrant'
        element={
          <ProtectedRoute allowedRoles={[fullAccessGroup]}>
            <Layout>
              <HeapGrant />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path='/available-accounts-list'
        element={
          <ProtectedRoute allowedRoles={[accessGroup, fullAccessGroup]}>
            <Layout>
              <AccountDetailsTable />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path='/emergencygrant'
        element={
          <ProtectedRoute allowedRoles={[fullAccessGroup]}>
            <Layout>
              <EmergencyGrant />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path='/account-details'
        element={
          <ProtectedRoute allowedRoles={[accessGroup, fullAccessGroup]}>
            <Layout>
              <AccountDetails />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path='/tendayhold'
        element={
          <ProtectedRoute allowedRoles={[fullAccessGroup]}>
            <Layout>
              <TenDayHold />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path='/account-inquiry-form'
        element={
          <ProtectedRoute allowedRoles={[accessGroup, fullAccessGroup]}>
            <Layout>
              <AccountInquiryForm />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path='/new-referral-forms'
        element={
          <ProtectedRoute allowedRoles={[fullAccessGroup]}>
            <Layout>
              <NewReferralForm />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path='/previously-viewed-forms'
        element={
          <ProtectedRoute allowedRoles={[fullAccessGroup]}>
            <Layout>
              <PreviouslyViewedForm />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path='/previously-viewed-forms-list'
        element={
          <ProtectedRoute allowedRoles={[fullAccessGroup]}>
            <Layout>
              <PreviouslyViewedFormList />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path='/startdirectvoucher'
        element={
          <ProtectedRoute allowedRoles={[fullAccessGroup]}>
            <Layout>
              <StartDirectVoucher />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path='/stopdirectvoucher'
        element={
          <ProtectedRoute allowedRoles={[fullAccessGroup]}>
            <Layout>
              <StopDirectVoucher />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path='/transferdirectvoucher'
        element={
          <ProtectedRoute allowedRoles={[fullAccessGroup]}>
            <Layout>
              <TransferDirectVoucher />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path='/startguarantee'
        element={
          <ProtectedRoute allowedRoles={[fullAccessGroup]}>
            <Layout>
              <StartGuarantee />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path='/stopguarantee'
        element={
          <ProtectedRoute allowedRoles={[fullAccessGroup]}>
            <Layout>
              <StopGuarantee />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path='/new-referral-forms-list'
        element={
          <ProtectedRoute allowedRoles={[fullAccessGroup]}>
            <Layout>
              <NewReferralFormList />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path='/outage/save'
        element={
          <ProtectedRoute allowedRoles={[adminAccessGroup]}>
            <Layout>
              <Outage />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route path='/outage' element={<OutagePage />} />

      <Route
        path='/outage/view'
        element={
          <ProtectedRoute allowedRoles={[adminAccessGroup]}>
            <Layout>
              <OutageView />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path='/bulletInBoard/save'
        element={
          <ProtectedRoute allowedRoles={[adminAccessGroup]}>
            <Layout>
              <BulletInBoardSave />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path='/bulletInBoard/view'
        element={
          <ProtectedRoute allowedRoles={[adminAccessGroup]}>
            <Layout>
              <BulletInBoardView />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path='/disclaimer/save'
        element={
          <ProtectedRoute allowedRoles={[adminAccessGroup]}>
            <Layout>
              <DisclaimerSave />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path='/disclaimer/view'
        element={
          <ProtectedRoute allowedRoles={[adminAccessGroup]}>
            <Layout>
              <DisclaimerView />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path='/contactus'
        element={
          <ProtectedRoute allowedRoles={[accessGroup, fullAccessGroup]}>
            <Layout>
              <ContactUs />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path='/femagrant'
        element={
          <ProtectedRoute allowedRoles={[fullAccessGroup]}>
            <Layout>
              <FemaGrant />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path='/careandshare'
        element={
          <ProtectedRoute allowedRoles={[fullAccessGroup]}>
            <Layout>
              <CareAndShare />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route path='*' element={<NoAccessPage />} />
    </Routes>
  );
};

const App = () => (
  <Router>
    <RouteConfig />
  </Router>
);

export default App;
